import React from 'react';
import { X, BarChart3, Eye, Mail, ShoppingCart, TrendingUp, DollarSign, Percent } from 'lucide-react';

export function FunnelAnalyticsModal({ funnel, analytics, onClose }) {
  const {
    views = 0,
    optIns = 0,
    purchases = 0,
    upsellAccepts = 0,
    upsellDeclines = 0,
    revenue = 0,
    optInRate = 0,
    purchaseRate = 0,
    upsellAcceptRate = 0
  } = analytics;

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency: 'GBP'
    }).format(amount);
  };

  const formatPercent = (value) => {
    return `${value.toFixed(1)}%`;
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div 
        className="bg-white rounded-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto relative"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="sticky top-0 bg-white border-b border-gray-200 p-6 flex items-center justify-between z-10">
          <div>
            <h2 className="text-2xl font-serif text-gray-800">Funnel Analytics</h2>
            <p className="text-sm text-gray-500 mt-1">{funnel.name}</p>
          </div>
          <button
            onClick={onClose}
            className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition"
          >
            <X size={24} />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6">
          {/* Key Metrics Grid */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {/* Views */}
            <div className="bg-blue-50 rounded-xl p-4 border border-blue-100">
              <div className="flex items-center gap-2 mb-2">
                <Eye className="text-blue-600" size={20} />
                <span className="text-sm font-medium text-blue-900">Views</span>
              </div>
              <p className="text-2xl font-bold text-blue-900">{views}</p>
            </div>

            {/* Opt-Ins */}
            <div className="bg-green-50 rounded-xl p-4 border border-green-100">
              <div className="flex items-center gap-2 mb-2">
                <Mail className="text-green-600" size={20} />
                <span className="text-sm font-medium text-green-900">Opt-Ins</span>
              </div>
              <p className="text-2xl font-bold text-green-900">{optIns}</p>
              {views > 0 && (
                <p className="text-xs text-green-700 mt-1">
                  {formatPercent(optInRate)} conversion
                </p>
              )}
            </div>

            {/* Purchases */}
            <div className="bg-purple-50 rounded-xl p-4 border border-purple-100">
              <div className="flex items-center gap-2 mb-2">
                <ShoppingCart className="text-purple-600" size={20} />
                <span className="text-sm font-medium text-purple-900">Purchases</span>
              </div>
              <p className="text-2xl font-bold text-purple-900">{purchases}</p>
              {optIns > 0 && (
                <p className="text-xs text-purple-700 mt-1">
                  {formatPercent(purchaseRate)} conversion
                </p>
              )}
            </div>

            {/* Revenue */}
            <div className="bg-yellow-50 rounded-xl p-4 border border-yellow-100">
              <div className="flex items-center gap-2 mb-2">
                <DollarSign className="text-yellow-600" size={20} />
                <span className="text-sm font-medium text-yellow-900">Revenue</span>
              </div>
              <p className="text-2xl font-bold text-yellow-900">{formatCurrency(revenue)}</p>
              {purchases > 0 && (
                <p className="text-xs text-yellow-700 mt-1">
                  {formatCurrency(revenue / purchases)} avg
                </p>
              )}
            </div>
          </div>

          {/* Upsell Metrics */}
          {(purchases > 0 || upsellAccepts > 0 || upsellDeclines > 0) && (
            <div className="bg-gray-50 rounded-xl p-6 border border-gray-200">
              <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
                <TrendingUp className="text-gray-600" size={20} />
                Upsell Performance
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <p className="text-sm text-gray-600 mb-1">Accepted</p>
                  <p className="text-2xl font-bold text-green-600">{upsellAccepts}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600 mb-1">Declined</p>
                  <p className="text-2xl font-bold text-red-600">{upsellDeclines}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600 mb-1">Acceptance Rate</p>
                  <p className="text-2xl font-bold text-gray-800">
                    {purchases > 0 ? formatPercent(upsellAcceptRate) : '0%'}
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Conversion Funnel Visualization */}
          {views > 0 && (
            <div className="bg-gray-50 rounded-xl p-6 border border-gray-200">
              <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
                <BarChart3 className="text-gray-600" size={20} />
                Conversion Funnel
              </h3>
              <div className="space-y-4">
                {/* Views to Opt-Ins */}
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-medium text-gray-700">Views → Opt-Ins</span>
                    <span className="text-sm text-gray-600">
                      {optIns} of {views} ({formatPercent(optInRate)})
                    </span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-3">
                    <div
                      className="bg-green-500 h-3 rounded-full transition-all"
                      style={{ width: `${Math.min((optIns / views) * 100, 100)}%` }}
                    />
                  </div>
                </div>

                {/* Opt-Ins to Purchases */}
                {optIns > 0 && (
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm font-medium text-gray-700">Opt-Ins → Purchases</span>
                      <span className="text-sm text-gray-600">
                        {purchases} of {optIns} ({formatPercent(purchaseRate)})
                      </span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-3">
                      <div
                        className="bg-purple-500 h-3 rounded-full transition-all"
                        style={{ width: `${Math.min((purchases / optIns) * 100, 100)}%` }}
                      />
                    </div>
                  </div>
                )}

                {/* Purchases to Upsells */}
                {purchases > 0 && (upsellAccepts > 0 || upsellDeclines > 0) && (
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm font-medium text-gray-700">Purchases → Upsell Accepts</span>
                      <span className="text-sm text-gray-600">
                        {upsellAccepts} of {purchases} ({formatPercent(upsellAcceptRate)})
                      </span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-3">
                      <div
                        className="bg-blue-500 h-3 rounded-full transition-all"
                        style={{ width: `${Math.min((upsellAccepts / purchases) * 100, 100)}%` }}
                      />
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Empty State */}
          {views === 0 && (
            <div className="text-center py-12">
              <BarChart3 className="mx-auto text-gray-400 mb-4" size={48} />
              <h3 className="text-lg font-semibold text-gray-800 mb-2">No Analytics Data Yet</h3>
              <p className="text-gray-600">
                This funnel hasn't received any traffic yet. Analytics will appear here once visitors start viewing your funnel.
              </p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="sticky bottom-0 bg-gray-50 border-t border-gray-200 p-4 flex justify-end">
          <button
            onClick={onClose}
            className="px-6 py-2 bg-[#c5a059] hover:bg-[#b08e4d] text-white rounded-lg transition"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}

